import {
  ActionButton,
  AnnualTravelCreditInfoBanner,
  AnnualTravelCreditInfoModal,
  getTravelCreditHistoryModalProps,
  TravelCreditHistoryModal,
  AnnualTravelCreditInfoBannerProps,
  getIsCreditAnnual,
} from "halifax";
import React from "react";
import clsx from "clsx";
import {
  RewardsAccount,
  Transaction,
  VIEWED_CREDIT_BENEFITS_MODAL,
  WalletDetailsResponse,
  CreditDetail,
  UserPreferenceKeyEnum,
  StatementCreditProductType,
  StatementCreditDetail,
} from "redmond";
import dayjs from "dayjs";
import {
  getConfigTenant,
  isCorpTenant,
  useGetUserMetadataQuery,
  useIsTravelApp,
  useUpdateUserCreditCampaignFtuxMetadataMutation,
} from "@capone/common";
import { CreditBannerInfo } from "halifax/build/AnnualTravelCreditInfoBanner/utils";
import { fetchTravelWalletCreditHistory } from "../../api/v1/takeovers/fetchTravelWalletCreditHistory";
import "./styles.scss";
import { trackEvent } from "../../api/v1/trackEvent";

interface HomepageAnnualTravelCreditProps {
  firstName: string;
  rewardsAccounts: RewardsAccount[];
  walletDetails: WalletDetailsResponse;
  showBanner?: boolean;
  isMobile?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  onChangeVisibleBanners?: AnnualTravelCreditInfoBannerProps["onChangeVisibleBanners"];
  onFtuxModalOpenChange?: (open: boolean) => void;
}

export const HomepageAnnualTravelCredit = ({
  firstName,
  rewardsAccounts,
  walletDetails,
  showBanner = true,
  isMobile = false,
  isAnnualTravelCreditsExperiment,
  onChangeVisibleBanners,
  onFtuxModalOpenChange,
}: HomepageAnnualTravelCreditProps) => {
  const [openCreditHistoryModal, setOpenCreditHistoryModal] =
    React.useState<boolean>(false);

  const isCorporate = isCorpTenant(getConfigTenant());

  const { isTravelApp } = useIsTravelApp();

  const [creditsTransactionHistory, setCreditsTransactionHistory] =
    React.useState<Transaction[]>([]);
  const [ftuxModalOpen, setFtuxModalOpen] = React.useState<boolean>(false);
  const [ftuxProducts, setFtuxProducts] = React.useState<{
    ventureX: boolean;
    ventureXBusiness: boolean;
  }>({ ventureX: false, ventureXBusiness: false });
  const [ftuxRTCModalOpen, setFTUXRTCModalOpen] =
    React.useState<boolean>(false);
  const [selectedRestrictedCredit, setSelectedRestrictedCredit] =
    React.useState<undefined | CreditDetail>(undefined);
  const [visibleCreditBanners, setVisibleCreditBanners] = React.useState<
    CreditBannerInfo[]
  >([]);

  const {
    data: userMetadata,
    isLoading: isUserMetadataLoading,
    refetch: refetchUserMetadata,
  } = useGetUserMetadataQuery(
    {
      keys: [UserPreferenceKeyEnum.creditCampaignFtux],
    },
    { skip: isTravelApp }
  );

  const [updateUserCreditCampaignFtuxMetadata] =
    useUpdateUserCreditCampaignFtuxMetadataMutation();

  const modalProps = getTravelCreditHistoryModalProps(
    firstName,
    creditsTransactionHistory,
    walletDetails.credit
  );

  React.useEffect(() => {
    onFtuxModalOpenChange?.(ftuxModalOpen);
  }, [ftuxModalOpen, onFtuxModalOpenChange]);

  React.useEffect(() => {
    if (
      !isTravelApp &&
      !isUserMetadataLoading &&
      visibleCreditBanners.length > 0
    ) {
      // show ftux modal for discover credits if not seen before
      const creditCampaignMetadata =
        userMetadata?.userMetadata?.creditCampaignFtux;

      if (creditCampaignMetadata != null) {
        const visibleDiscoverCreditIds = visibleCreditBanners
          .flatMap((banner) => banner.products)
          .filter((product) =>
            [
              StatementCreditProductType.DiscoverItCashBack,
              StatementCreditProductType.DiscoverItChrome,
              StatementCreditProductType.DiscoverItMiles,
              StatementCreditProductType.DiscoverItMore,
            ].includes(product.productType)
          )
          .map((product) => product.capOneCreditId);

        const unseenCreditId = visibleDiscoverCreditIds.find((creditId) => {
          const creditMetadata = creditCampaignMetadata.find(
            (metadata) => metadata.caponeCreditId === creditId
          );
          return !creditMetadata || creditMetadata.hasSeen === false;
        });

        if (unseenCreditId) {
          const creditToShow: StatementCreditDetail | undefined =
            walletDetails.creditBreakdown
              .filter((credit) => credit.CreditDetail === "Statement")
              .find(
                (credit) =>
                  credit.CreditDetail === "Statement" &&
                  credit.capOneCreditId.value === unseenCreditId
              ) as unknown as StatementCreditDetail; // force type cast to fix type checking in CI

          if (creditToShow) {
            setSelectedRestrictedCredit(creditToShow);
            setFtuxModalOpen(true);
            updateUserCreditCampaignFtuxMetadata({
              hasSeen: true,
              caponeCreditId: creditToShow.capOneCreditId.value,
            }).then(() => refetchUserMetadata());
          }
        }
      }
    }
  }, [
    userMetadata?.userMetadata?.creditCampaignFtux,
    visibleCreditBanners,
    isTravelApp,
    isUserMetadataLoading,
  ]);

  React.useEffect(() => {
    fetchTravelWalletCreditHistory().then((response) => {
      setCreditsTransactionHistory(response.credits);
    });
  }, []);

  React.useEffect(() => {
    const lsItem = localStorage.getItem("atc_ftux_modal_shown");
    const modalShown = lsItem === "true";

    if (!modalShown) {
      const hasPreviouslyCreatedStatementCredit =
        !!walletDetails.creditBreakdown.find(
          (creditDetail) =>
            creditDetail.CreditDetail === "Statement" &&
            dayjs().isAfter(creditDetail.createdOn, "day")
        );

      // don't show FTUX if user already had a credit before
      if (!hasPreviouslyCreatedStatementCredit) {
        const vxStatementCreditCreatedToday =
          walletDetails.creditBreakdown.some(
            (creditDetail) =>
              creditDetail.CreditDetail === "Statement" &&
              creditDetail.productType === "VentureX" &&
              getIsCreditAnnual(creditDetail) &&
              dayjs().isSame(creditDetail.createdOn, "day")
          );

        const vxbStatementCreditCreatedToday =
          walletDetails.creditBreakdown.some(
            (creditDetail) =>
              creditDetail.CreditDetail === "Statement" &&
              creditDetail.productType === "VentureXBusiness" &&
              getIsCreditAnnual(creditDetail) &&
              dayjs().isSame(creditDetail.createdOn, "day")
          );

        if (vxStatementCreditCreatedToday || vxbStatementCreditCreatedToday) {
          setFtuxProducts({
            ventureX: vxStatementCreditCreatedToday,
            ventureXBusiness: vxbStatementCreditCreatedToday,
          });
          setFtuxModalOpen(true);
          trackEvent({
            eventName: VIEWED_CREDIT_BENEFITS_MODAL,
            properties: {},
          });
          localStorage.setItem("atc_ftux_modal_shown", "true");
        }
      }
    }
  }, [walletDetails.creditBreakdown, rewardsAccounts]);

  if (isCorporate) {
    return null;
  }

  return (
    <div
      className={clsx("home-page-atc-info-banner-container", {
        mobile: isMobile,
        "show-banner": showBanner,
      })}
    >
      {showBanner && (
        <>
          <AnnualTravelCreditInfoBanner
            variant={isMobile ? "small" : "large"}
            firstName={firstName}
            rewardsAccounts={rewardsAccounts}
            creditBreakdown={walletDetails.creditBreakdown}
            onClickCTA={() => setOpenCreditHistoryModal(true)}
            hasCreditTransactionHistory={!!creditsTransactionHistory.length}
            onChangeVisibleBanners={(banners) => {
              setVisibleCreditBanners(banners);
              onChangeVisibleBanners?.(banners);
            }}
            onHowItWorksClick={(credit) => {
              setSelectedRestrictedCredit(credit);
              setFTUXRTCModalOpen(true);
            }}
          />

          {isMobile && !!creditsTransactionHistory.length && (
            <ActionButton
              className="b2b"
              message="View travel credits"
              onClick={() => setOpenCreditHistoryModal(true)}
            />
          )}
          <TravelCreditHistoryModal
            modalContent={modalProps.modalContentProps}
            openCreditHistoryModal={openCreditHistoryModal}
            setOpenCreditHistoryModal={setOpenCreditHistoryModal}
            creditTransactionsHistory={modalProps.creditTransactionsHistory}
            isMobile={isMobile}
            rewardsAccounts={rewardsAccounts}
            credit={walletDetails.credit}
            creditBreakdown={walletDetails.creditBreakdown}
            isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
          />

          <AnnualTravelCreditInfoModal
            hasVentureX={ftuxProducts.ventureX}
            hasVentureXBusiness={ftuxProducts.ventureXBusiness}
            open={
              ftuxModalOpen || (selectedRestrictedCredit && ftuxRTCModalOpen)
            }
            setOpen={
              ftuxRTCModalOpen
                ? (open: boolean) => {
                    setFTUXRTCModalOpen(open);
                    setSelectedRestrictedCredit(undefined);
                  }
                : setFtuxModalOpen
            }
            isMobile={isMobile}
            credit={selectedRestrictedCredit}
          />
        </>
      )}
    </div>
  );
};
